import React, { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  useSlotGroup,
  useGallery,
  useProperty,
  useContentToken,
  RichTextContent,
  EditableSlotGroup,
  EditableToken,
} from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  normalizeBedroomDescription,
  resolveBedroomOptionsLabel,
} from '../utils/bedrooms';

// ============================================================================
// Inlined helpers (parent theme doesn't export these hooks / GlowTrace)
// ============================================================================

function useScrollReveal<T extends HTMLElement = HTMLElement>(
  options?: { threshold?: number; rootMargin?: string },
): RefObject<T> {
  const ref = useRef<T>(null) as RefObject<T>;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    el.classList.add('reveal--hidden');

    const raf = requestAnimationFrame(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.classList.remove('reveal--hidden');
            el.classList.add('reveal--visible');
            observer.unobserve(el);
          }
        },
        {
          threshold: options?.threshold ?? 0.15,
          rootMargin: options?.rootMargin ?? '0px 0px -40px 0px',
        },
      );
      observer.observe(el);
    });

    return () => cancelAnimationFrame(raf);
  }, [options?.threshold, options?.rootMargin]);

  return ref;
}

const REVEAL_STYLE_ID = 'avenue-reveal-animations';

function useRevealStyles(): void {
  useEffect(() => {
    if (document.getElementById(REVEAL_STYLE_ID)) return;
    // The parent theme injects the full stylesheet elsewhere; this override
    // just ensures the minimum fade-up rule exists if we're rendered alone.
    const style = document.createElement('style');
    style.id = REVEAL_STYLE_ID;
    style.textContent = `
      .reveal-fade-up.reveal--hidden { opacity: 0; transform: translateY(24px); }
      .reveal-fade-up.reveal--visible {
        opacity: 1; transform: translateY(0);
        transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
                    transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
      }
      .glow-trace-wrap { position: relative; }
      .glow-trace-svg { position: absolute; inset: 0; width: 100%; height: 100%; z-index: 20; pointer-events: none; overflow: visible; }
      .glow-trace-svg rect {
        fill: none; stroke: var(--vibe-color-primary); stroke-width: 2; rx: 2; ry: 2;
        filter: drop-shadow(0 0 4px var(--vibe-color-primary))
                drop-shadow(0 0 8px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent));
      }
      @media (prefers-reduced-motion: reduce) {
        .reveal-fade-up.reveal--hidden { opacity: 1; transform: none; }
        .glow-trace-svg rect { opacity: 0 !important; animation: none !important; }
      }
    `;
    document.head.appendChild(style);
  }, []);
}

function GlowTrace({ delay = 0.3 }: { delay?: number }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<SVGRectElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0, perim: 0 });
  const hasAnimated = useRef(false);

  const measure = useCallback(() => {
    const el = wrapRef.current?.parentElement;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    if (width > 0 && height > 0) {
      setDims({ w: width, h: height, perim: 2 * (width + height) });
    }
  }, []);

  useEffect(() => {
    const el = wrapRef.current?.parentElement;
    if (!el) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    if (dims.perim === 0 || hasAnimated.current) return;
    const rect = rectRef.current;
    const wrap = wrapRef.current;
    if (!rect || !wrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const target = wrap.parentElement || wrap;

    const runAnimation = () => {
      if (hasAnimated.current) return;
      hasAnimated.current = true;

      rect.style.strokeDasharray = `${dims.perim}`;
      rect.style.strokeDashoffset = `${dims.perim}`;
      rect.style.opacity = '0';

      rect.animate(
        [
          { strokeDashoffset: `${dims.perim}`, opacity: 0 },
          { strokeDashoffset: `${dims.perim}`, opacity: 0.85, offset: 0.04 },
          { strokeDashoffset: `${dims.perim * 0.08}`, opacity: 0.85, offset: 0.72 },
          { strokeDashoffset: '0', opacity: 0 },
        ],
        {
          duration: 2200,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          delay: delay * 1000,
          fill: 'forwards',
        },
      );
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          runAnimation();
        }
      },
      { threshold: 0.3, rootMargin: '0px 0px -40px 0px' },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [dims.perim, delay]);

  const inset = 1;
  const ready = dims.perim > 0;

  return (
    <div
      ref={wrapRef}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20, overflow: 'visible' }}
    >
      {ready && (
        <svg className="glow-trace-svg" viewBox={`0 0 ${dims.w} ${dims.h}`} preserveAspectRatio="none">
          <rect
            ref={rectRef}
            x={inset}
            y={inset}
            width={dims.w - inset * 2}
            height={dims.h - inset * 2}
            style={{ opacity: 0 }}
          />
        </svg>
      )}
    </div>
  );
}

// ============================================================================
// Smart Photo Selection
// ============================================================================

const CARD_KEYWORDS: Record<string, string[]> = {
  floorplans: ['interior', 'apartment', 'unit', 'bedroom', 'living room', 'kitchen', 'bathroom', 'floor plan', 'model', 'home', 'layout', 'spacious'],
  gallery: ['community', 'building', 'property', 'exterior', 'entrance', 'lobby', 'leasing', 'welcome', 'amenity', 'pool', 'lounge'],
  // Second floor-plans CTA — favor furnished / lifestyle interiors so it
  // doesn't compete with card1's layout-oriented picks.
  availability: ['living room', 'furnished', 'dining', 'open concept', 'natural light', 'balcony', 'patio', 'view', 'sunlit', 'cozy', 'modern'],
};

function scorePhoto(photo: PhotoGallery, keywords: string[]): number {
  const searchText = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!searchText) return 0;
  return keywords.reduce((score, kw) => score + (searchText.includes(kw) ? 1 : 0), 0);
}

function findBestPhotoIndex(
  gallery: PhotoGallery[],
  cardKey: string,
  usedIndices: Set<number>,
  fallbackIndex: number,
): number {
  const keywords = CARD_KEYWORDS[cardKey] || [];
  if (!gallery.length || !keywords.length) return fallbackIndex;

  let bestIndex = -1;
  let bestScore = 0;

  for (let i = 0; i < gallery.length; i++) {
    if (usedIndices.has(i)) continue;
    const score = scorePhoto(gallery[i], keywords);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }

  if (bestIndex >= 0) return bestIndex;

  for (let i = fallbackIndex; i < gallery.length; i++) {
    if (!usedIndices.has(i)) return i;
  }
  for (let i = 0; i < fallbackIndex; i++) {
    if (!usedIndices.has(i)) return i;
  }

  return Math.min(fallbackIndex, Math.max(gallery.length - 1, 0));
}

function useSmartPhotoIndices(): [number, number, number] {
  const gallery = useGallery();
  return useMemo(() => {
    const used = new Set<number>();
    const keys = ['floorplans', 'gallery', 'availability'] as const;
    const fallbacks = [1, 3, 5];
    const indices: number[] = [];
    for (let i = 0; i < keys.length; i++) {
      const idx = findBestPhotoIndex(gallery, keys[i], used, fallbacks[i]);
      used.add(idx);
      indices.push(idx);
    }
    return indices as [number, number, number];
  }, [gallery]);
}

function makeSlotDef(label: string, index: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// ============================================================================
// Static slot group definitions (for contentSchema)
// ============================================================================

const card1ImageDef: SlotGroupDefinition = {
  label: 'Floor Plans Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card2ImageDef: SlotGroupDefinition = {
  label: 'Gallery Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card3ImageDef: SlotGroupDefinition = {
  label: 'Availability Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card1LinkDef: SlotGroupDefinition = {
  label: 'Floor Plans Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Choose Your Floorplan' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card2LinkDef: SlotGroupDefinition = {
  label: 'Gallery Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Browse The Gallery' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card3LinkDef: SlotGroupDefinition = {
  label: 'Availability Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'See Availability' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// CTA Card Sub-component
// ============================================================================

function CTACard({
  imageUrl,
  imageAlt,
  titleNode,
  descNode,
  linkNode,
  glowDelay = 0.3,
}: {
  imageUrl?: string | null;
  imageAlt?: string | null;
  titleNode: React.ReactNode;
  descNode: React.ReactNode;
  linkNode: React.ReactNode;
  glowDelay?: number;
}) {
  const imgCls = 'absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105';
  return (
    <div className="cta-card glow-trace-wrap relative overflow-hidden group">
      <GlowTrace delay={glowDelay} />
      {imageUrl ? (
        <VibesitesImage url={imageUrl} alt={imageAlt || ''} className={imgCls} sizes="(max-width: 768px) 100vw, 33vw" />
      ) : (
        <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
      )}
      <div className="absolute inset-0 cta-card-overlay" />
      <div className="relative z-10 flex flex-col justify-end h-full p-5 sm:p-6 lg:p-7">
        {titleNode}
        {descNode}
        {linkNode}
      </div>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function CTASection() {
  const property = useProperty();
  const [idx1, idx2, idx3] = useSmartPhotoIndices();

  const def1 = useMemo(() => makeSlotDef('Floor Plans Image', idx1), [idx1]);
  const def2 = useMemo(() => makeSlotDef('Gallery Image', idx2), [idx2]);
  const def3 = useMemo(() => makeSlotDef('Availability Image', idx3), [idx3]);

  const img1 = useSlotGroup('ctasection.card1_image', def1);
  const img2 = useSlotGroup('ctasection.card2_image', def2);
  const img3 = useSlotGroup('ctasection.card3_image', def3);

  const link1 = useSlotGroup('ctasection.card1_link', card1LinkDef);
  const link2 = useSlotGroup('ctasection.card2_link', card2LinkDef);
  const link3 = useSlotGroup('ctasection.card3_link', card3LinkDef);

  // Expand property_bedrooms_range into listed bedroom numbers so embedded
  // field tokens read "1, 2, & 3 bedrooms" instead of raw ranges like "1-3".
  const bedroomOptionsLabel = useMemo(
    () =>
      resolveBedroomOptionsLabel(
        property.floorplans,
        property.property_bedrooms_range,
      ),
    [property.floorplans, property.property_bedrooms_range],
  );

  const resolveProperty = useMemo(
    () => ({
      ...(property as Record<string, unknown>),
      property_bedrooms_range:
        bedroomOptionsLabel ?? property.property_bedrooms_range ?? '',
    }),
    [property, bedroomOptionsLabel],
  );

  const resolveBedroomDesc = useCallback(
    (raw: string | null | undefined) => {
      if (!raw) return '';
      let html = resolveRichText(raw, resolveProperty as Record<string, any>);
      // Mirror RichTextContent: strip a single wrapping <p> when rendering as="p".
      const trimmed = html.trim();
      const wrap = trimmed.match(/^<p>([\s\S]*)<\/p>$/);
      if (wrap && !/<p[\s>]|<div[\s>]|<h[1-6][\s>]/i.test(wrap[1])) {
        html = wrap[1];
      }
      // Hide redundant bed/bedroom wording; expand any leftover dashed ranges.
      return normalizeBedroomDescription(html, bedroomOptionsLabel);
    },
    [resolveProperty, bedroomOptionsLabel],
  );

  const card1DescRaw = useContentToken('ctasection.card1.description');
  const card1DescResolved = useMemo(
    () => resolveBedroomDesc(card1DescRaw),
    [card1DescRaw, resolveBedroomDesc],
  );

  const card3DescRaw = useContentToken('ctasection.card3.description');
  const card3DescResolved = useMemo(
    () => resolveBedroomDesc(card3DescRaw),
    [card3DescRaw, resolveBedroomDesc],
  );

  useRevealStyles();
  const gridRef = useScrollReveal<HTMLDivElement>({ threshold: 0.3 });

  const titleCls = 'font-heading text-sm sm:text-base font-bold uppercase tracking-[0.15em] text-white mb-2 drop-shadow-lg';
  const descCls = 'font-body text-xs sm:text-sm text-white mb-4 leading-relaxed drop-shadow-md line-clamp-2';
  const btnCls = 'cta-card-btn inline-flex items-center justify-center self-start';

  return (
    <section className="w-full bg-white">
      <div ref={gridRef} className="reveal-fade-up grid grid-cols-1 md:grid-cols-3 gap-2 p-2">
        <EditableSlotGroup groupPath="ctasection.card1_image" definition={def1}>
          <CTACard
            imageUrl={img1.image}
            imageAlt={img1.alt}
            titleNode={
              <RichTextContent path="ctasection.card1.title" label="Card 1 Title" defaultValue="<p>Spacious Layouts</p>" as="h2" className={titleCls} />
            }
            descNode={
              <EditableToken
                path="ctasection.card1.description"
                type="richtext"
                label="Card 1 Description"
                as="p"
                className={descCls}
              >
                <span
                  data-vibesite-richtext=""
                  dangerouslySetInnerHTML={{ __html: card1DescResolved }}
                  style={{ display: 'contents' }}
                />
              </EditableToken>
            }
            linkNode={
              <EditableSlotGroup groupPath="ctasection.card1_link" definition={card1LinkDef}>
                <a href={link1.url || '#'} target={link1._target || '_self'} className={`${btnCls} cta-card-btn--card1`}>
                  {link1.label || 'Learn More'}
                </a>
              </EditableSlotGroup>
            }
            glowDelay={0.3}
          />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="ctasection.card2_image" definition={def2}>
          <CTACard
            imageUrl={img2.image}
            imageAlt={img2.alt}
            titleNode={
              <RichTextContent path="ctasection.card2.title" label="Card 2 Title" defaultValue="<p>Picture Yourself Here</p>" as="h2" className={titleCls} />
            }
            descNode={
              <RichTextContent path="ctasection.card2.description" label="Card 2 Description" defaultValue="<p>See what life at our community is all about.</p>" as="p" className="font-body text-xs sm:text-sm text-white mb-4 leading-relaxed drop-shadow-md line-clamp-2" />
            }
            linkNode={
              <EditableSlotGroup groupPath="ctasection.card2_link" definition={card2LinkDef}>
                <a href={link2.url || '#'} target={link2._target || '_self'} className={`${btnCls} cta-card-btn--card2`}>
                  {link2.label || 'Learn More'}
                </a>
              </EditableSlotGroup>
            }
            glowDelay={0.6}
          />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="ctasection.card3_image" definition={def3}>
          <CTACard
            imageUrl={img3.image}
            imageAlt={img3.alt}
            titleNode={
              <RichTextContent path="ctasection.card3.title" label="Card 3 Title" defaultValue="<p>Find Your Fit</p>" as="h2" className={titleCls} />
            }
            descNode={
              <EditableToken
                path="ctasection.card3.description"
                type="richtext"
                label="Card 3 Description"
                as="p"
                className={descCls}
              >
                <span
                  data-vibesite-richtext=""
                  dangerouslySetInnerHTML={{ __html: card3DescResolved }}
                  style={{ display: 'contents' }}
                />
              </EditableToken>
            }
            linkNode={
              <EditableSlotGroup groupPath="ctasection.card3_link" definition={card3LinkDef}>
                <a href={link3.url || '/floor-plans'} target={link3._target || '_self'} className={`${btnCls} cta-card-btn--card3`}>
                  {link3.label || 'See Availability'}
                </a>
              </EditableSlotGroup>
            }
            glowDelay={0.9}
          />
        </EditableSlotGroup>
      </div>

      <style>{`
        .cta-card {
          height: 28vh;
          min-height: 200px;
          max-height: 320px;
          border-radius: 4px;
        }
        .cta-card-overlay {
          background: linear-gradient(
            to top,
            color-mix(in srgb, var(--vibe-color-primary) 55%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 30%, rgba(0,0,0,0.5)) 40%,
            color-mix(in srgb, var(--vibe-color-primary) 10%, rgba(0,0,0,0.15)) 70%,
            transparent 100%
          );
        }
        .cta-card-btn {
          padding: 0.5rem 1.5rem;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          color: #ffffff;
          background: rgba(255, 255, 255, 0.22);
          border: 1px solid rgba(255, 255, 255, 0.55);
          -webkit-backdrop-filter: blur(6px);
          backdrop-filter: blur(6px);
          border-radius: 9999px;
          transition: all 0.3s ease;
          white-space: nowrap;
        }
        .cta-card-btn:hover {
          background: rgba(255, 255, 255, 0.9);
          color: var(--vibe-color-text);
          border-color: rgba(255, 255, 255, 0.95);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CTASection',
  displayName: 'CTA Cards',
  tokens: {
    'ctasection.card1.title': {
      label: 'Card 1 Title',
      type: 'richtext',
      defaultValue: '<p>Spacious Layouts</p>',
    },
    'ctasection.card1.description': {
      label: 'Card 1 Description',
      type: 'richtext',
      defaultValue:
        '<p>Choose from our spacious <span data-type="field-token" data-field="property_bedrooms_range"></span> floor plans.</p>',
      description:
        'Supports the property_bedrooms_range field token. At render, ranges expand into listed options (e.g. "1 & 2 bedrooms" or "1, 2, & 3 bedrooms") and redundant bed/bedroom wording is removed.',
    },
    'ctasection.card2.title': {
      label: 'Card 2 Title',
      type: 'richtext',
      defaultValue: '<p>Picture Yourself Here</p>',
    },
    'ctasection.card2.description': {
      label: 'Card 2 Description',
      type: 'richtext',
      defaultValue: '<p>See what life at our community is all about.</p>',
    },
    'ctasection.card3.title': {
      label: 'Card 3 Title',
      type: 'richtext',
      defaultValue: '<p>Find Your Fit</p>',
    },
    'ctasection.card3.description': {
      label: 'Card 3 Description',
      type: 'richtext',
      defaultValue: '<p>Explore available homes and pricing that work for you.</p>',
      description:
        'Supports the property_bedrooms_range field token. At render, ranges expand into listed options (e.g. "1 & 2 bedrooms" or "1, 2, & 3 bedrooms") and redundant bed/bedroom wording is removed.',
    },
  },
  slotGroups: {
    'ctasection.card1_image': card1ImageDef,
    'ctasection.card2_image': card2ImageDef,
    'ctasection.card3_image': card3ImageDef,
    'ctasection.card1_link': card1LinkDef,
    'ctasection.card2_link': card2LinkDef,
    'ctasection.card3_link': card3LinkDef,
  },
};

(CTASection as any).contentSchema = contentSchema;

export default CTASection;
